import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";

// Schemas + cores live in `./messages-cores.ts` — kept separate so this
// file stays under the 400-line ceiling per `.claude/rules/code-locality.md`.
// Both client mutators (here) and the server-mutator extension
// (`server/server-mutators.ts`) call the same cores.
import {
  messageInsertSchema,
  messageInsertCore,
  messageAddReplySchema,
  messageAddReplyCore,
  messageAddInlineReplySchema,
  messageAddInlineReplyCore,
  messageUpdateSchema,
  messageUpdateCore,
  attachmentInsertSchema,
  messageInsertWithAttachmentsSchema,
  messageInsertWithAttachmentsCore,
  messageAddReplyWithAttachmentsSchema,
  messageAddReplyWithAttachmentsCore,
  messageAddInlineReplyWithAttachmentsSchema,
  messageAddInlineReplyWithAttachmentsCore,
} from "./messages-cores";

// Re-export so `@ano/shared/mutators` (the barrel in `./index.ts`) keeps
// the same import surface for callers that pull schemas/cores directly.
export {
  messageInsertSchema,
  messageInsertCore,
  messageAddReplySchema,
  messageAddReplyCore,
  messageAddInlineReplySchema,
  messageAddInlineReplyCore,
  messageUpdateSchema,
  messageUpdateCore,
  attachmentInsertSchema,
  messageInsertWithAttachmentsSchema,
  messageInsertWithAttachmentsCore,
  messageAddReplyWithAttachmentsSchema,
  messageAddReplyWithAttachmentsCore,
  messageAddInlineReplyWithAttachmentsSchema,
  messageAddInlineReplyWithAttachmentsCore,
} from "./messages-cores";

export const messagesMutators = {
  insert: defineMutator(messageInsertSchema, async (params) => {
    await messageInsertCore(params);
  }),
  addReply: defineMutator(messageAddReplySchema, async (params) => {
    await messageAddReplyCore(params);
  }),
  // Discord-shape inline reply — distinct from `addReply`. Caller picks
  // at the call site:
  //   • Hover-bar "Reply" / composer pill → `addInlineReply`.
  //   • Sidebar composer (after "Create thread") → `addReply`.
  // No counter bump; the row sits in the channel timeline citing the
  // immediate parent via `replied_to_message_id`.
  addInlineReply: defineMutator(messageAddInlineReplySchema, async (params) => {
    await messageAddInlineReplyCore(params);
  }),
  // Phase 7b PR D1 — atomic message+attachments variants. The whole
  // mutator body runs inside one Zero transaction server-side, so any
  // attachment INSERT failure rolls back the message INSERT too. Eliminates
  // the partial-failure window where `kind='image'` rows could end up
  // with `payload.items=[ghost-id]` and no matching attachment rows.
  // Server override in `server/server-mutators.ts` adds post-insert
  // effects (notifications, unread counts, embedding queue, etc.).
  insertWithAttachments: defineMutator(
    messageInsertWithAttachmentsSchema,
    async (params) => {
      await messageInsertWithAttachmentsCore(params);
    },
  ),
  addReplyWithAttachments: defineMutator(
    messageAddReplyWithAttachmentsSchema,
    async (params) => {
      await messageAddReplyWithAttachmentsCore(params);
    },
  ),
  // Atomic inline-reply + attachments — same atomicity guarantee as
  // `addReplyWithAttachments` but for the Discord-shape inline citation.
  // Replaces the prior non-atomic "insert inline reply, then fire-and-forget
  // attachment inserts" path that could leave a media-kind inline message
  // with no attachment row.
  addInlineReplyWithAttachments: defineMutator(
    messageAddInlineReplyWithAttachmentsSchema,
    async (params) => {
      await messageAddInlineReplyWithAttachmentsCore(params);
    },
  ),
  update: defineMutator(messageUpdateSchema, async (params) => {
    await messageUpdateCore(params);
  }),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const msg = await tx.run(zql.messages.where("id", args.id).one());
        if (!msg) return;
        if (msg.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not message author");
        }
      }
      await tx.mutate.messages.delete(args);
    },
  ),
};

export const reactionsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      message_id: z.string(),
      user_id: z.string(),
      emoji: z.string(),
      created_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: user_id mismatch");
      }
      if (tx.location === "server") {
        const msg = await tx.run(
          zql.messages.where("id", args.message_id).one(),
        );
        if (!msg) throw new Error("Message not found");
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", msg.channel_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) {
          throw new Error("Unauthorized: not a channel member");
        }
      }
      // Idempotency across ALL clients: migration 164 added a UNIQUE index on
      // (message_id, user_id, emoji) to back the bridge's ON CONFLICT insert.
      // That constraint applies to every reactions writer, so a duplicate
      // optimistic/redelivered insert (e.g. a rapid double-click reading a
      // stale reactionList) would otherwise violate UNIQUE and throw on the
      // server push — rolling back the optimistic reaction and degrading the
      // core UX. Look up an existing row for the same identity and no-op
      // instead of inserting. MUST run AFTER the membership gate above, else a
      // non-member's call short-circuits here and is silently dropped instead
      // of rejected. Runs identically on client and server so both the
      // optimistic apply and the authoritative replay agree.
      const existing = await tx.run(
        zql.reactions
          .where("message_id", args.message_id)
          .where("user_id", args.user_id)
          .where("emoji", args.emoji)
          .one(),
      );
      if (existing) return;
      await tx.mutate.reactions.insert(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.reactions.where("id", args.id).one());
        if (!entity) return;
        if (entity.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.reactions.delete(args);
    },
  ),
};

export const linkPreviewsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      message_id: z.string(),
      url: z.string(),
      title: z.string().optional().nullable(),
      description: z.string().optional().nullable(),
      image_url: z.string().optional().nullable(),
      favicon_url: z.string().optional().nullable(),
      site_name: z.string().optional().nullable(),
      fetched_at: z.number(),
      created_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      // Channel membership check — derive channel from message
      if (tx.location === "server") {
        const msg = await tx.run(
          zql.messages.where("id", args.message_id).one(),
        );
        if (!msg) throw new Error("Message not found");
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", msg.channel_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) {
          throw new Error("Unauthorized: not a channel member");
        }
      }
      await tx.mutate.link_previews.insert(args);
    },
  ),
  delete: defineMutator(z.object({ id: z.string() }), async () => {
    throw new Error("Unauthorized: link preview deletion is server-only");
  }),
};

export const attachmentsMutators = {
  insert: defineMutator(attachmentInsertSchema, async ({ tx, args, ctx }) => {
    if (ctx?.sub && args.user_id !== ctx.sub) {
      throw new Error("Unauthorized: user_id mismatch");
    }
    if (tx.location === "server") {
      const msg = await tx.run(zql.messages.where("id", args.message_id).one());
      if (!msg) throw new Error("Message not found");
      const membership = await tx.run(
        zql.channel_members
          .where("channel_id", msg.channel_id)
          .where("user_id", ctx.sub)
          .where("removed_at", "IS", null)
          .one(),
      );
      if (!membership) {
        throw new Error("Unauthorized: not a channel member");
      }
    }
    // WS-A1 slice 3b: coerce missing/null storage_url → "" (see
    // insertAttachmentsForMessage in messages-cores.ts for rationale).
    await tx.mutate.attachments.insert({
      ...args,
      storage_url: args.storage_url ?? "",
    });
  }),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.attachments.where("id", args.id).one());
        if (!entity) return;
        if (entity.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.attachments.delete(args);
    },
  ),
};
